import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import {
    ArrowLeft, Download, Trash2, User, BookOpen, Heart,
    MessageSquare, BarChart3, Shield, ChevronDown, ChevronUp,
    RefreshCw, CheckCircle, Clock, AlertCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Section = ({ icon, title, count, children, defaultOpen = false }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <Card className="overflow-hidden border-gray-200">
            <button
                onClick={() => setOpen(v => !v)}
                className="w-full p-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
            >
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center text-purple-600">
                        {icon}
                    </div>
                    <div>
                        <span className="font-semibold text-gray-900">{title}</span>
                        {count !== undefined && (
                            <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">
                                {count} {count === 1 ? 'registro' : 'registros'}
                            </span>
                        )}
                    </div>
                </div>
                {open
                    ? <ChevronUp className="w-5 h-5 text-gray-400" />
                    : <ChevronDown className="w-5 h-5 text-gray-400" />}
            </button>
            {open && (
                <div className="px-5 pb-5 border-t border-gray-100">
                    <div className="pt-4">{children}</div>
                </div>
            )}
        </Card>
    );
};

const Field = ({ label, value }) => (
    <div className="flex flex-col sm:flex-row sm:items-center gap-1 py-2 border-b border-gray-50 last:border-0">
        <span className="text-xs font-medium text-gray-500 sm:w-44 flex-shrink-0">{label}</span>
        <span className="text-sm text-gray-800 break-all">{value ?? '—'}</span>
    </div>
);

const MeusDados = () => {
    const navigate = useNavigate();
    const { user, logout } = useAuth();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isDownloading, setIsDownloading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteEmail, setDeleteEmail] = useState('');
    const [deleteError, setDeleteError] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await axios.get(`${API}/auth/account/export`, { withCredentials: true });
            setData(res.data);
        } catch (err) {
            setError('Não foi possível carregar seus dados. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    const handleDownload = async () => {
        setIsDownloading(true);
        try {
            const res = await axios.get(`${API}/auth/account/export?download=true`, {
                withCredentials: true,
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'meus-dados-simby.json');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Download error:', err);
        } finally {
            setIsDownloading(false);
        }
    };

    const handleDelete = async () => {
        if (deleteEmail.trim().toLowerCase() !== (user?.email || '').toLowerCase()) {
            setDeleteError('O e-mail não corresponde ao da sua conta.');
            return;
        }
        setIsDeleting(true);
        setDeleteError('');
        try {
            await axios.delete(`${API}/auth/account`, { withCredentials: true });
            await logout();
            navigate('/', { replace: true });
        } catch (err) {
            setDeleteError('Erro ao excluir conta. Tente novamente ou contate o suporte.');
            setIsDeleting(false);
        }
    };

    const fmt = (iso) => {
        if (!iso) return '—';
        try { return new Date(iso).toLocaleString('pt-BR'); }
        catch { return iso; }
    };

    const perfil = data?.perfil || {};

    return (
        <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

                {/* Header */}
                <div className="flex items-center gap-4 mb-8">
                    <Button variant="ghost" onClick={() => navigate(-1)} className="text-gray-600">
                        <ArrowLeft className="w-5 h-5 mr-1" />
                        Voltar
                    </Button>
                    <div className="flex items-center gap-2">
                        <Shield className="w-6 h-6 text-purple-600" />
                        <h1 className="text-2xl font-bold text-gray-900">Meus Dados</h1>
                    </div>
                </div>

                {/* LGPD Notice */}
                <Card className="mb-6 p-4 bg-purple-50 border-purple-200">
                    <p className="text-sm text-purple-800">
                        <strong>LGPD — Art. 18:</strong> Estes são todos os dados que a Simby possui sobre você.
                        Você tem direito a visualizá-los, baixá-los em formato portátil ou excluir sua conta permanentemente.
                    </p>
                </Card>

                {/* Action Bar */}
                <div className="flex flex-wrap gap-3 mb-6">
                    <Button
                        variant="outline"
                        className="border-purple-200 text-purple-700 hover:bg-purple-50"
                        onClick={handleDownload}
                        disabled={isDownloading || loading}
                    >
                        <Download className="w-4 h-4 mr-2" />
                        {isDownloading ? 'Gerando arquivo...' : 'Baixar meus dados (JSON)'}
                    </Button>
                    <Button
                        variant="outline"
                        className="border-red-200 text-red-600 hover:bg-red-50"
                        onClick={() => { setShowDeleteModal(true); setDeleteEmail(''); setDeleteError(''); }}
                    >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Excluir minha conta
                    </Button>
                    {!loading && (
                        <Button variant="ghost" onClick={fetchData} className="text-gray-500">
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Atualizar
                        </Button>
                    )}
                </div>

                {/* Loading */}
                {loading && (
                    <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600 mb-4" />
                        <p>Carregando seus dados...</p>
                    </div>
                )}

                {/* Error */}
                {error && (
                    <Card className="p-4 border-red-200 bg-red-50 mb-4">
                        <div className="flex items-center gap-2 text-red-700">
                            <AlertCircle className="w-5 h-5" />
                            <span className="text-sm">{error}</span>
                        </div>
                    </Card>
                )}

                {/* Data Sections */}
                {data && (
                    <div className="space-y-3">

                        {/* Export metadata */}
                        <p className="text-xs text-gray-400 text-right">
                            Exportado em: {fmt(data.exportado_em)}
                        </p>

                        {/* Perfil */}
                        <Section icon={<User className="w-5 h-5" />} title="Perfil" defaultOpen>
                            <div className="space-y-0">
                                <Field label="Nome" value={perfil.name} />
                                <Field label="E-mail" value={perfil.email} />
                                <Field label="Empresa" value={perfil.group} />
                                <Field label="Departamento" value={perfil.department} />
                                <Field label="Conta criada em" value={fmt(perfil.created_at)} />
                                <Field label="Último acesso" value={fmt(perfil.last_active_date)} />
                                <Field label="Termos aceitos em" value={fmt(perfil.terms_accepted_at)} />
                                <Field label="Versão dos termos" value={perfil.terms_version} />
                            </div>
                        </Section>

                        {/* Progresso */}
                        <Section
                            icon={<BookOpen className="w-5 h-5" />}
                            title="Progresso em Lições"
                            count={data.progresso_licoes?.length}
                        >
                            {data.progresso_licoes?.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-gray-500 border-b">
                                                <th className="pb-2 font-medium">Módulo / Lição</th>
                                                <th className="pb-2 font-medium">XP</th>
                                                <th className="pb-2 font-medium">Concluído em</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.progresso_licoes.slice(0, 50).map((r, i) => (
                                                <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                                                    <td className="py-2 font-mono text-xs text-gray-600">
                                                        {r.module_id || r.lesson_id || '—'}
                                                    </td>
                                                    <td className="py-2 text-green-700 font-medium">{r.xp_earned ?? '—'}</td>
                                                    <td className="py-2 text-gray-500">{fmt(r.completed_at)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {data.progresso_licoes.length > 50 && (
                                        <p className="text-xs text-gray-400 mt-2">
                                            Mostrando 50 de {data.progresso_licoes.length}. Baixe o JSON para ver tudo.
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <p className="text-sm text-gray-400">Nenhum progresso registrado ainda.</p>
                            )}
                        </Section>

                        {/* Quiz Results */}
                        <Section
                            icon={<CheckCircle className="w-5 h-5" />}
                            title="Resultados de Quiz"
                            count={data.resultados_quiz?.length}
                        >
                            {data.resultados_quiz?.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-gray-500 border-b">
                                                <th className="pb-2 font-medium">Módulo</th>
                                                <th className="pb-2 font-medium">Pontuação</th>
                                                <th className="pb-2 font-medium">Data</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.resultados_quiz.slice(0, 50).map((r, i) => (
                                                <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                                                    <td className="py-2 font-mono text-xs text-gray-600">{r.module_id || '—'}</td>
                                                    <td className="py-2">
                                                        <span className={`font-medium ${(r.score || 0) >= 70 ? 'text-green-700' : 'text-orange-600'}`}>
                                                            {r.score != null ? `${r.score}%` : '—'}
                                                        </span>
                                                    </td>
                                                    <td className="py-2 text-gray-500">{fmt(r.completed_at || r.created_at)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <p className="text-sm text-gray-400">Nenhum resultado de quiz ainda.</p>
                            )}
                        </Section>

                        {/* Diário Emocional */}
                        <Section
                            icon={<Heart className="w-5 h-5" />}
                            title="Diário Emocional"
                            count={data.diario_emocional?.length}
                        >
                            {data.diario_emocional?.length > 0 ? (
                                <div className="space-y-3">
                                    {data.diario_emocional.slice(0, 10).map((entry, i) => (
                                        <div key={i} className="p-3 bg-gray-50 rounded-lg text-sm">
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="font-medium text-gray-700">
                                                    {entry.emotion || entry.trigger || 'Entrada'}
                                                </span>
                                                <span className="text-xs text-gray-400">{fmt(entry.created_at)}</span>
                                            </div>
                                            {entry.situation && (
                                                <p className="text-gray-600 text-xs mt-1">
                                                    <strong>Situação:</strong> {entry.situation}
                                                </p>
                                            )}
                                            {entry.reflection && (
                                                <p className="text-gray-600 text-xs mt-1">
                                                    <strong>Reflexão:</strong> {entry.reflection}
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                    {data.diario_emocional.length > 10 && (
                                        <p className="text-xs text-gray-400">
                                            Mostrando 10 de {data.diario_emocional.length}. Baixe o JSON para ver tudo.
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <p className="text-sm text-gray-400">Nenhuma entrada no diário ainda.</p>
                            )}
                        </Section>

                        {/* Check-ins */}
                        <Section
                            icon={<Clock className="w-5 h-5" />}
                            title="Check-ins Diários"
                            count={data.checkins_diarios?.length}
                        >
                            {data.checkins_diarios?.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-gray-500 border-b">
                                                <th className="pb-2 font-medium">Data</th>
                                                <th className="pb-2 font-medium">Humor</th>
                                                <th className="pb-2 font-medium">Energia</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.checkins_diarios.slice(0, 30).map((c, i) => (
                                                <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                                                    <td className="py-2 text-gray-500">{fmt(c.date || c.created_at)}</td>
                                                    <td className="py-2">{c.mood ?? c.humor ?? '—'}</td>
                                                    <td className="py-2">{c.energy ?? c.energia ?? '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {data.checkins_diarios.length > 30 && (
                                        <p className="text-xs text-gray-400 mt-2">
                                            Mostrando 30 de {data.checkins_diarios.length}. Baixe o JSON para ver tudo.
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <p className="text-sm text-gray-400">Nenhum check-in registrado.</p>
                            )}
                        </Section>

                        {/* Simuladores */}
                        <Section
                            icon={<MessageSquare className="w-5 h-5" />}
                            title="Simulações com IA"
                            count={data.simuladores?.length}
                        >
                            {data.simuladores?.length > 0 ? (
                                <div className="space-y-3">
                                    {data.simuladores.slice(0, 10).map((s, i) => (
                                        <div key={i} className="p-3 bg-gray-50 rounded-lg text-sm">
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="font-medium text-gray-700">
                                                    {s.scenario_title || s.scenario_id || `Simulação ${i + 1}`}
                                                </span>
                                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                                    s.passed ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                                                }`}>
                                                    {s.score != null ? `${s.score}%` : '—'}
                                                </span>
                                            </div>
                                            <p className="text-xs text-gray-400">{fmt(s.completed_at || s.created_at)}</p>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-gray-400">Nenhuma simulação realizada ainda.</p>
                            )}
                        </Section>

                        {/* Insights */}
                        {data.insights_emocionais?.length > 0 && (
                            <Section
                                icon={<BarChart3 className="w-5 h-5" />}
                                title="Insights Emocionais"
                                count={data.insights_emocionais.length}
                            >
                                <p className="text-sm text-gray-500 mb-2">
                                    Insights gerados automaticamente a partir do seu Diário Emocional.
                                </p>
                                <div className="space-y-2">
                                    {data.insights_emocionais.slice(0, 5).map((ins, i) => (
                                        <div key={i} className="p-3 bg-purple-50 rounded-lg text-sm">
                                            <p className="text-gray-700">{ins.insight || ins.content || JSON.stringify(ins)}</p>
                                            <p className="text-xs text-gray-400 mt-1">{fmt(ins.created_at)}</p>
                                        </div>
                                    ))}
                                </div>
                            </Section>
                        )}

                    </div>
                )}

                {/* DPO Contact */}
                <Card className="mt-6 p-4 border-gray-200 bg-gray-50">
                    <p className="text-xs text-gray-500">
                        Dúvidas sobre seus dados? Contate nosso DPO:{' '}
                        <a href="mailto:dpo@paulapontvianne.com.br" className="text-purple-600 underline">
                            dpo@paulapontvianne.com.br
                        </a>
                    </p>
                </Card>
            </div>

            {/* Delete Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <Card className="bg-white p-6 max-w-md w-full rounded-2xl shadow-2xl">
                        <h3 className="text-xl font-bold text-red-600 mb-2">Excluir minha conta</h3>
                        <p className="text-gray-600 mb-4 text-sm">
                            Esta ação é <strong>permanente e irreversível</strong>. Todos os seus dados serão
                            excluídos imediatamente, incluindo:
                        </p>
                        <ul className="text-sm text-gray-600 mb-4 space-y-1">
                            <li>• Perfil e progresso em lições</li>
                            <li>• Diário Emocional e check-ins</li>
                            <li>• Simulações com IA</li>
                            <li>• Histórico completo</li>
                        </ul>
                        <p className="text-xs text-gray-400 mb-4">
                            Dados de pagamento podem ser retidos pelo prazo exigido pela legislação fiscal.
                        </p>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Digite seu e-mail para confirmar:
                            </label>
                            <input
                                type="email"
                                value={deleteEmail}
                                onChange={e => { setDeleteEmail(e.target.value); setDeleteError(''); }}
                                placeholder={user?.email}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
                            />
                        </div>
                        {deleteError && <p className="text-sm text-red-600 mb-3">{deleteError}</p>}
                        <div className="flex gap-3">
                            <Button
                                variant="ghost"
                                className="flex-1"
                                onClick={() => setShowDeleteModal(false)}
                                disabled={isDeleting}
                            >
                                Cancelar
                            </Button>
                            <Button
                                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                                onClick={handleDelete}
                                disabled={isDeleting || deleteEmail.trim().toLowerCase() !== (user?.email || '').toLowerCase()}
                            >
                                {isDeleting ? 'Excluindo...' : 'Excluir conta'}
                            </Button>
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
};

export default MeusDados;
